import {
  ShowContextProvider,
  Title,
  useEditController,
  RichTextField,
} from "react-admin";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { IdChip } from "../../components/IdChip";
import { colors, fontFamilyMono, radius } from "../../data/tokens";
import { mailTriggers, TriggerChip } from "./mail-templates-util";
import { SendTestEmailButton } from "./SendTestEmailButton";

const labelSx = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: colors.textMuted,
};

// Ficha do template: nome + badge do gatilho, o que o disparo faz, e o
// corpo numa prévia com moldura de e-mail — a versão antiga empilhava
// campos crus (com o id no topo) sem hierarquia.
export const MailTemplateShow = () => {
  const { id } = useParams();
  const controller: any = useEditController({
    resource: "mail-templates/crud",
    id,
  });
  const record = controller?.record;
  const known = record ? mailTriggers[record.trigger] : undefined;

  return (
    <ShowContextProvider value={controller}>
      <Title title="Templates de E-mail" />
      <PageTopBreadcrumb
        title="Templates de E-mail"
        firstPath="/#/mail-templates/crud"
        secondTitle="Visualizando"
        thirdTitle={record?.name}
        actionType="edit"
        actions={record ? <SendTestEmailButton record={record} /> : undefined}
      />
      {record && (
        <Card sx={{ marginBottom: 3 }}>
          {/* Box em vez de CardContent: o App.css zera o padding de todo
              .MuiCardContent-root com !important */}
          <Box sx={{ padding: { xs: 2.5, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: colors.primaryDark,
                }}
              >
                {record.name}
              </Typography>
              <TriggerChip trigger={record.trigger} />
            </Box>
            <Typography
              sx={{ fontSize: 12.5, color: colors.textMuted, marginTop: 0.5 }}
            >
              {known
                ? known.description
                : "Este gatilho não é disparado automaticamente pelo servidor — o template ainda pode ser enviado pelo envio manual em massa (Sócios › Iniciar Operações em Massa)."}
            </Typography>

            <Divider sx={{ marginY: 2.5 }} />

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography sx={labelSx}>Assunto do e-mail</Typography>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: colors.textStrong,
                  }}
                >
                  {record.subject}
                </Typography>
              </Box>
              <Box>
                <Typography sx={labelSx}>Gatilho técnico</Typography>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    color: colors.textStrong,
                    fontFamily: fontFamilyMono,
                  }}
                >
                  {record.trigger}
                </Typography>
              </Box>
              {record.createdAt && (
                <Box>
                  <Typography sx={labelSx}>Criado em</Typography>
                  <Typography
                    sx={{ fontSize: 13.5, color: colors.textStrong }}
                  >
                    {new Date(record.createdAt).toLocaleDateString("pt-BR")}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography sx={labelSx}>Identificador técnico</Typography>
                <Box sx={{ marginTop: 0.5 }}>
                  <IdChip value={record.id} />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ marginY: 2.5 }} />

            <Typography sx={labelSx}>Corpo do e-mail</Typography>
            {/* Prévia com moldura de e-mail; a classe richTextBody
                mantém o CSS das listas do conteúdo (App.css) */}
            <Box
              className="richTextBody"
              sx={{
                border: `1px solid ${colors.border}`,
                borderRadius: `${radius.md}px`,
                backgroundColor: colors.bgSubtle,
                padding: "16px 20px",
                marginTop: 1,
                fontSize: 14,
                "& p": { margin: "0 0 8px" },
              }}
            >
              <RichTextField source="body" label={false} />
            </Box>
            {known && record.trigger === "ACCOUNT_RECOVERY" && (
              <Typography
                sx={{ fontSize: 12, color: colors.textMuted, marginTop: 0.75 }}
              >
                O link de recuperação é acrescentado automaticamente após este
                texto no envio.
              </Typography>
            )}
          </Box>
        </Card>
      )}
    </ShowContextProvider>
  );
};
